import { client } from "./supabase";

// ─── COSTOS DE LOTE (componentes de costo / landed cost de una compra) ────────
//
// Espejo estructural de planPagosActivos.js, pero colgado de lotes_compra(id) vía
// lote_costos.lote_id. Diferencia conceptual clave con las cuotas: las cuotas
// PARTICIONAN un mismo monto (Σ cuotas == costo_unitario, es financiación); los
// componentes de landed cost SUMAN al valor (FOB + transporte + aduana capitalizan
// a la base depreciable del activo). Cada componente lleva su propia moneda y su
// propia fecha_pago. Doctrina (ver ARQUITECTURA 9.36 / 4.13 / 10.100): pagar un
// componente es CAJA PURA (movimiento_bancario_id + fecha_pago_real + estado='pagado');
// la capitalización al activo va por la ejecución del lote, aparte.
//
// Fase A commit 1: solo datos + utils. Sin UI, sin proyección, sin conciliación.

export const getCostosDeLote = async (loteId) => {
  const { data, error } = await client
    .from("lote_costos")
    .select("*")
    .eq("lote_id", loteId)
    .order("fecha_pago")
    .order("id");
  if (error) throw error;
  return data || [];
};

// Set de lote_id que tienen AL MENOS un componente (cualquier estado). Necesario aparte de "tiene
// componentes pendientes": si el landed cost está completo (todos pagados), el lote sigue teniendo
// componentes y NO debe volver a proyectar la línea única del lote. Lo usará la bifurcación de CapEx.
export const getLotesConCostos = async (pais) => {
  const { data, error } = await client.from("lote_costos").select("lote_id").eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.lote_id));
};

export const crearCostosLote = async (costos) => {
  const { data, error } = await client.from("lote_costos").insert(costos).select();
  if (error) throw error;
  return data || [];
};

// Edita UN componente por id, SOLO si está pendiente (el `.eq("estado","pendiente")` es el candado en
// DB). Nunca toca componentes pagados — están vinculados a un movimiento histórico vía
// movimiento_bancario_id. La transición pendiente→pagado de la conciliación SÍ pasa (parte de un row
// pendiente); lo que bloquea es re-editar uno ya pagado. Si el componente está pagado (o no existe),
// no edita nada y lanza error. (Espejo del candado de eliminarCuotaActivo.)
export const actualizarCostoLote = async (id, cambios) => {
  const { data, error } = await client
    .from("lote_costos")
    .update(cambios)
    .eq("id", id)
    .eq("estado", "pendiente")
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo editar el costo ${id} (¿ya está pagado?)`);
  return data[0];
};

// Borra UN componente por id, SOLO si está pendiente (mismo candado en DB). Nunca toca pagados.
// Si el componente está pagado (o no existe), no borra nada y lanza error. (Espejo de eliminarCuotaActivo.)
export const eliminarCostoLote = async (id) => {
  const { data, error } = await client.from("lote_costos").delete().eq("id", id).eq("estado", "pendiente").select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar el costo ${id} (¿ya está pagado?)`);
  return data[0];
};

// Reversión de componente: lo devuelve a 'pendiente' limpiando el vínculo bancario. Se usará al
// desvincular un componente del banco (desvincular = revertir, patrón de revertirCuotaActivo). NO
// lleva candado de estado a propósito: opera sobre componentes ya pagados.
export const revertirCostoLote = async (id) => {
  const { data, error } = await client
    .from("lote_costos")
    .update({ estado: "pendiente", fecha_pago_real: null, movimiento_bancario_id: null })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ─── Componentes futuros pendientes (proyección CapEx) ────────────────────────
//
// Filtra estado='pendiente' + país; SIN filtro de fecha → incluye vencidos (patrón 10.155). Enriquece
// con el contexto del lote (descripción, cantidad, tipo, proveedor) y descarta componentes de lotes
// borrados (soft-delete no dispara el CASCADE). El consumidor (proyección CapEx) arma la línea por
// componente leyendo `c.lote`.
//
// FETCH EN DOS PASOS (deliberado, NO un embed `!inner`): `lote_costos` tiene RLS off, pero `lotes_compra`
// tiene RLS on. Un embed `lotes_compra!inner` acopla esta query al cache de relaciones FK de PostgREST
// y a la RLS del padre; con el FK recién creado, durante la ventana de refresco del cache el embed
// devolvía 400 y el componente desaparecía silenciosamente de CapEx. El patrón `.in(ids)` que ya usa
// getLotes es inmune a eso: dos queries planas sobre tablas que la app lee sin problema.
export const getCostosLotesFuturos = async (pais) => {
  const { data: costos, error } = await client
    .from("lote_costos")
    .select("*")
    .eq("estado", "pendiente")
    .eq("pais", pais)
    .order("fecha_pago");
  if (error) throw error;
  if (!costos || costos.length === 0) return [];

  const loteIds = [...new Set(costos.map((c) => c.lote_id).filter((id) => id != null))];
  const { data: lotes, error: errLotes } = await client
    .from("lotes_compra")
    .select("id, pais, tipo_activo, descripcion, cantidad_total, moneda, eliminado, proveedor:proveedores(id, nombre)")
    .in("id", loteIds);
  if (errLotes) throw errLotes;

  const loteById = new Map((lotes || []).map((l) => [l.id, l]));
  // eliminado=false client-side (soft-delete no dispara el CASCADE); si el lote no es visible/existe,
  // el componente se descarta (mismo efecto que el viejo !inner, sin depender del embed).
  return costos
    .map((c) => ({ ...c, lote: loteById.get(c.lote_id) || null }))
    .filter((c) => c.lote && c.lote.eliminado === false);
};
